<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { notNull } from "@/helpers/utility/Type";

import InputNumber from "@core/input/InputNumber.vue";
import InputDropdown from "@core/input/InputDropdown.vue";
import InputStateToggle from "@core/input/InputStateToggle.vue";

import { useCondition } from "./useCondition";
import { useSettingsStore } from "@editor/store/settings.store";
import { formatRoundName } from "../simulationRound/useRoundTime";
import { createFilledArray } from "@/helpers/utility/Array";
import { getValidComparedEndTypes } from "./conditionFunctions";

import type { ConditionItem } from "./ConditionItem";

interface Props {
  conditionId: number;
}
const props = defineProps<Props>();

const ctx = useCondition(() => props.conditionId);
const { t } = useI18n();
const settingsStore = useSettingsStore();

const roundUnit = computed(() => settingsStore.simulationRoundUnit);

const roundUnitName = computed(() => {
  return t(`roundUnits.${roundUnit.value}`);
});

function formatRoundIndex(index: number) {
  return formatRoundName(
    settingsStore.simulationDurationType,
    settingsStore.simulationRoundUnit,
    settingsStore.simulationRoundLength,
    index,
    settingsStore.simulationTimeStart,
    false
  );
}

const allAvailableRoundIndices = computed(() => {
  return createFilledArray(settingsStore.simulationRoundAmount);
});

const usedTimeForm = computed(() => {
  if (ctx.comparedTimeForm == "point") return "point";
  return ctx.condition.comparedEndType == null ? "point" : "range";
});

const needsTime = computed(() => {
  if (
    (
      ["valueChange", "increase", "decrease", "activationCount"] as (
        | string
        | null
      )[]
    ).includes(ctx.condition.comparedProperty)
  ) {
    return true;
  }

  return false;
});

/**
 * Is it possible to optionally set a start/end time?
 */
const timeOptional = computed(() => {
  // No type? No time:
  if (!ctx.supportsComparedTime) return false;
  if (needsTime.value) return false;

  // Types where setting the time is not possible:
  if (ctx.condition.comparedProperty == "activationTime") {
    return false;
  }

  // Everything else may be specified with time.
  return true;
});

const rangeOptional = computed(() => {
  if (!ctx.supportsComparedTime) return false;
  if (ctx.comparedTimeForm == "point") return false;
  return true;
});

const startVisible = computed(() => {
  if (!ctx.supportsComparedTime) {
    return false;
  }
  if (needsTime.value) return true;
  return timeOptional.value && ctx.condition.comparedStartType != null;
});

const validComparedStartTypeOptions = computed(() => {
  return ctx.validComparedStartTypes.map((type) => ({
    value: type,
    label: t(`labels.conditionTimeType.${type}`)
  }));
});
const selectedComparedStartTypeOption = computed(() => {
  return validComparedStartTypeOptions.value.find(
    (o) => o.value == ctx.condition.comparedStartType
  );
});

const startOptions = computed(() => {
  return allAvailableRoundIndices.value.filter((i) =>
    ctx.condition.comparedEnd ? i <= ctx.condition.comparedEnd : true
  ).map((i) => ({
    value: i,
    label: formatRoundIndex(i)
  }));
});
const comparedStartOption = computed(() => {
  return startOptions.value.find(
    (o) => o.value == ctx.condition.comparedStart
  );
});

function setStartType(type: ConditionItem["comparedStartType"]) {
  const startValue = type == "relativeRound" ? 1 : 0;
  ctx.validUpdate({
    comparedStartType: type,
    comparedStart: startValue
  });
}

function setStart(value: number) {
  const changes: Partial<ConditionItem> = {
    comparedStart: value
  };

  // If the start type is relative, the end must be at least as high as the start.
  if (ctx.condition.comparedStartType == ctx.condition.comparedEndType) {
    if (ctx.condition.comparedStartType == "absoluteRound") {
      if (
        ctx.condition.comparedEnd != null &&
        ctx.condition.comparedEnd < value
      ) {
        changes.comparedEnd = value;
      }
    }
  }

  ctx.validUpdate(changes);
}

function updateTimeForm(form: "range" | "point") {
  if (form == "range") {
    ctx.validUpdate({
      comparedEndType: "currentRound",
      comparedEnd: 0
    });
  } else {
    ctx.validUpdate({
      comparedEndType: null
    });
  }
}

const endVisible = computed(() => {
  // there's only an end if there's a start.
  if (!startVisible.value) return;

  return usedTimeForm.value == "range";
});

const endOptions = computed(() => {
  let filtered = allAvailableRoundIndices.value;

  if (
    ctx.condition.comparedStartType == "startRound" ||
    ctx.condition.comparedStartType == "currentRound"
  ) {
    // don't filter
  } else {
    filtered = filtered.filter(
      (i) => i >= (ctx.condition.comparedStart || 0)
    );
  }

  return filtered.map((i) => ({
    value: i,
    label: formatRoundIndex(i)
  }));
});
const comparedEndOption = computed(() => {
  return endOptions.value.find((o) => o.value == ctx.condition.comparedEnd);
});
const endTypeOptions = computed(() => {
  return getValidComparedEndTypes(ctx.condition).filter(notNull).map((type) => ({
    value: type,
    label: t(`labels.conditionTimeType.${type}`)
  }));
});
const selectedEndTypeOption = computed(() => {
  return endTypeOptions.value.find(
    (o) => o.value == ctx.condition.comparedEndType
  );
});
</script>

<template>
  <div v-if="ctx.comparedItem && ctx.supportsComparedTime" class="column pt-2">
    <input-state-toggle
      v-if="rangeOptional"
      v-slot="{ state }"
      :states="['point', 'range']"
      :value="usedTimeForm"
      @update:value="updateTimeForm($event)"
    >
      {{ $t(`labels.conditionTimeForm.${state}`) }}
    </input-state-toggle>

    <div v-if="startVisible">
      <div
        :class="
          ctx.condition.comparedStartType == 'startRound' ||
          ctx.condition.comparedStartType == 'currentRound'
            ? 'line'
            : 'r-50-50'
        "
      >
        <input-dropdown
          v-if="validComparedStartTypeOptions.length > 1"
          :value="selectedComparedStartTypeOption"
          :options="validComparedStartTypeOptions"
          :label="
            usedTimeForm == 'range'
              ? $t('labels.conditionStart')
              : $t('labels.conditionTime')
          "
          :searchable="true"
          class="w-full"
          prop="label"
          item-key="value"
          @update:value="setStartType($event.value)"
        ></input-dropdown>
        <input-dropdown
          v-if="ctx.condition.comparedStartType == 'absoluteRound'"
          :options="startOptions"
          :value="comparedStartOption"
          :label="roundUnitName"
          :searchable="true"
          prop="label"
          item-key="value"
          @update:value="setStart($event.value)"
        />
        <input-number
          v-else-if="ctx.condition.comparedStartType == 'relativeRound'"
          :label="$t(`roundUnits.${roundUnit}`, 4)"
          :value="ctx.condition.comparedStart"
          :min="1"
          @update:value="setStart($event)"
        />
      </div>
    </div>

    <div v-if="endVisible">
      <div
        :class="
          ctx.condition.comparedEndType == 'currentRound' ? 'line' : 'r-50-50'
        "
      >
        <input-dropdown
          v-if="endTypeOptions.length > 1"
          :value="selectedEndTypeOption"
          :options="endTypeOptions"
          :label="$t('labels.conditionEnd')"
          :searchable="true"
          class="w-full"
          prop="label"
          item-key="value"
          @update:value="ctx.validUpdateKey('comparedEndType', $event.value)"
        ></input-dropdown>
        <input-dropdown
          v-if="ctx.condition.comparedEndType == 'absoluteRound'"
          :options="endOptions"
          :value="comparedEndOption"
          :label="roundUnitName"
          :searchable="true"
          prop="label"
          item-key="value"
          @update:value="ctx.validUpdateKey('comparedEnd', $event.value)"
        />
        <input-number
          v-else-if="ctx.condition.comparedEndType == 'relativeRound'"
          :value="ctx.condition.comparedEnd"
          :label="$t(`roundUnits.${roundUnit}`, 4)"
          :min="1"
          @update:value="ctx.validUpdateKey('comparedEnd', $event)"
        />
      </div>
    </div>
  </div>
</template>
